import React from 'react';
import { connect } from 'react-redux';
import pt from 'prop-types';

import auth from 'services/auth';
import LoginLayout from 'components/login/layout';
import LoadingView from 'components/view/loading';

import actions from 'partners/actions';
import AcceptStorePage from 'partners/components/pages/login/accept-store';

const mapStateToProps = (state) => ({
  flash: state.flash,
});

const mapDispatchToProps = (dispatch) => ({
  fetchStoreInvite: (code) => {
    return dispatch(actions.partner.fetchStoreInvite(code));
  },

  fetchStoreInvitePartners: (code) => {
    return dispatch(actions.partner.fetchStoreInvitePartners(code));
  },

  switchPartner: (partnerId, path) => {
    return dispatch(actions.user.switchPartner(partnerId, path));
  },

  flashError: (message) => {
    dispatch(actions.flash.error(message));
  },
});

// The invite whose login just landed back here
let loggedInInviteCode = null;

// The invite whose login this tab started: a Google/GitHub login hands its
// session back only to that tab, never to a link carrying someone else's
const LOGIN_STARTED_KEY = 'accept_store_login';

function setLoginStarted(code) {
  try {
    window.sessionStorage.setItem(LOGIN_STARTED_KEY, code);
  } catch (err) {
    // No storage: only the password login can come back
  }
}

function takeLoginStarted() {
  try {
    const code = window.sessionStorage.getItem(LOGIN_STARTED_KEY);
    window.sessionStorage.removeItem(LOGIN_STARTED_KEY);
    return code;
  } catch (err) {
    return null;
  }
}

/**
 * A store's partner invite, emailed by the store. Whatever the cookies, the
 * user always logs in with the regular partner login, then picks a partner
 * dashboard here, on the general (unprefixed) host. The store is connected
 * once they are in the picked dashboard (`AcceptStoreConnect`).
 *
 * Steps (`route.step`):
 * - `start`, `/accept-store/:code`: checks the invite, then sends the user to
 *   `/login?to=/accept-store/:code`
 * - `login`, `/:partnerId/accept-store/:code`: where the login lands (the
 *   router prefixes the session's partner); moves on to `select` before
 *   anything renders (`onEnter`)
 * - `select`, `/accept-store/:code/select`: lists the user's partner
 *   dashboards. A Google/GitHub login lands here directly, with its session
 *   in the url hash
 *
 * Moving between steps happens in `onEnter`, before the app loads the
 * session's partner: the steps share this component, which react-router
 * would otherwise keep mounted from one step to the next.
 */
export class AcceptStore extends React.PureComponent {
  static propTypes = {
    params: pt.shape({ code: pt.string }).isRequired,
    route: pt.shape({ step: pt.string }).isRequired,
    location: pt.object,
    router: pt.object,
    fetchStoreInvite: pt.func,
    fetchStoreInvitePartners: pt.func,
    switchPartner: pt.func,
    flashError: pt.func,
  };

  static onEnter(_store, nextState, replace) {
    const { step } = nextState.routes[nextState.routes.length - 1];
    const { partnerId, code } = nextState.params;

    if (step === 'login') {
      // The session the login just started, in its partner's cookie
      const sessionId = auth.getSession();

      if (!sessionId) {
        replace(`/accept-store/${code}`);
        return;
      }

      // Keep using it on the general host, where the url names no partner
      auth.setLoggedInPartnerSessionId(sessionId, partnerId);
      loggedInInviteCode = code;
      replace(`/accept-store/${code}/select`);
    } else if (step === 'select') {
      const loginSession = new URLSearchParams(
        (nextState.location?.hash || '').replace(/^#/, ''),
      );
      const loginPartnerId = loginSession.get('partner_id');
      const loginSessionId = loginSession.get('session_id');

      if (loginPartnerId && loginSessionId && takeLoginStarted() === code) {
        auth.setLoggedInPartnerSessionId(loginSessionId, loginPartnerId);
        loggedInInviteCode = code;
        replace(`/accept-store/${code}/select`);
      } else if (loggedInInviteCode !== code) {
        replace(`/accept-store/${code}`);
      }
    }
  }

  constructor(props) {
    super(props);

    this.state = {
      loaded: false,
      storeInvite: null,
      storeInvitePartners: null,
      onSelectAcceptPartner: this.onSelectAcceptPartner.bind(this),
    };
  }

  async componentDidMount() {
    const { params, route, location, router, fetchStoreInvite } = this.props;
    const { code } = params;

    const storeInvite = (await fetchStoreInvite(code)) || {
      error: { message: 'This invitation link is invalid' },
    };

    if (storeInvite.error) {
      this.setState({ loaded: true, storeInvite });
      return;
    }

    if (route.step === 'select') {
      await this.loadPartners(storeInvite);
      return;
    }

    if (location?.query?.no_partner) {
      // The login found no partner dashboard for the user
      this.setState({ loaded: true, storeInvite, storeInvitePartners: [] });
      return;
    }

    setLoginStarted(code);
    router.originalReplace(
      `/login?to=${encodeURIComponent(`/accept-store/${code}`)}`,
    );
  }

  async loadPartners(storeInvite) {
    const { params, fetchStoreInvitePartners } = this.props;
    const result = await fetchStoreInvitePartners(params.code);

    this.setState({
      loaded: true,
      storeInvite: result?.error ? result : storeInvite,
      storeInvitePartners: result?.results || [],
    });
  }

  async onSelectAcceptPartner(partnerId) {
    const { params, switchPartner, flashError } = this.props;
    try {
      // Reloads into the picked partner dashboard, which connects the store
      const result = await switchPartner(
        partnerId,
        `/accept-store/${params.code}/connect`,
      );

      if (!result) {
        flashError('Access denied');
      }
    } catch (err) {
      flashError(err?.message || 'Access denied');
    }
  }

  render() {
    if (!this.state.loaded) {
      return <LoadingView />;
    }

    return (
      <LoginLayout
        {...this.props}
        {...this.state}
        className="login-partner-container"
      >
        <AcceptStorePage {...this.props} {...this.state} />
      </LoginLayout>
    );
  }
}

export default connect(mapStateToProps, mapDispatchToProps)(AcceptStore);
